import {Tooltip, Button as MButton} from '@mantine/core'
import {UIState, useUIItem} from 'components/hooks/useUIState'
import {usePortal} from 'components/portal/portal'
import {SAvatarGroup} from 'components/portal/SAvatar'
import {DateTime} from 'luxon'
import {HiCheckCircle, HiLightBulb, HiMinusCircle} from 'react-icons/hi'
import {entries, keys, mapToObj, mapValues} from 'remeda'
import {compactObj} from 'shared/collection'
import {NotNull, notNull} from 'shared/type'
import {doSafe} from 'shared/promise'
import {zx} from 'shared/type'
import {match} from 'ts-pattern'
import {z} from 'zod'
import {useQuestionUIItem} from 'components/question/useQuestions'

export namespace CommentReaction {
  type ReactionMd = {Icon: React.ComponentType; anon?: boolean; color?: string}
  const md = {
    convinced: {
      Icon: HiCheckCircle,
      anon: true,
      color: 'green'
    },
    unconvinced: {
      Icon: HiMinusCircle,
      anon: true,
      color: 'red'
    },
    insightful: {
      Icon: HiLightBulb,
      color: 'yellow'
    } as ReactionMd
  } satisfies Record<string, ReactionMd>
  export type Type = keyof typeof md
  export const values = entries(md).map(([type, md]) => ({type, ...md}))
  export const types = keys(md)

  export const zCfg = z
    .record(zx.enumFromObjKeys(md), z.object({at: z.string()}).nullish())
    .describe('reaction')
    .default({})

  /**
   * Configuration for aggregate rows about reactions
   * see db function `ui_workspace_update_aggregates` for details about how ui_users rows trigger
   * updates to the ui_worksapce to make this cfg
   */
  export const zCfgAgg = z
    .record(zx.enumFromObjKeys(md), z.object({count: z.number(), by: z.array(z.string()).nullish()}).nullish())
    .describe('reaction-agg')

  export type ReactionAggCfg = z.infer<typeof zCfgAgg>

  export const useCommentReactions = ({comment_id}: {comment_id: string}) => {
    const {profile, profilesUI} = usePortal()
    const wsReactions = useQuestionUIItem({schema: zCfg, subPath: [comment_id], mode: 'workspace'})
    const userReactions = useQuestionUIItem({
      schema: zCfg,
      subPath: [comment_id],
      debounceSave: 0
    })
    const wsReactionSummary = useQuestionUIItem({schema: zCfgAgg, subPath: [comment_id], mode: 'workspace'})
    const reactionSummary: Partial<Record<Type, {count: number; by?: string[] | null}>> =
      doSafe(wsReactionSummary.values, s => compactObj(s)) ?? {}

    const setReaction = (type: Type, on: boolean) => {
      const at = DateTime.now().toISO()
      const newValues =
        on ?
          {
            ...match(type)
              .with(
                'convinced',
                'unconvinced',
                t => ({...userReactions.values, [t == 'convinced' ? 'unconvinced' : 'convinced']: null}) // as a special case, toggle off the semetrical reaction
              )
              .otherwise(() => userReactions.values),
            [type]: {at}
          }
        : {...userReactions.values, [type]: null}
      userReactions.setValues(newValues)
    }

    const reactions = mapToObj(types, t => {
      const summary = reactionSummary[t]
      const userReaction = userReactions.values?.[t]
      const wsReaction = wsReactions.values?.[t]
      const active = userReaction != null
      return [
        t,
        {
          ...summary,
          ...wsReaction,
          active,
          type: t,
          onClick: () => setReaction(t, !active),
          byProfiles: summary?.by
            ?.map(p => profilesUI?.[p])
            .filter(notNull)
            .map(p => ({...p, isYou: p.id == profile.id}))
        }
      ]
    })

    return {setReaction, reactions}
  }

  export type DisplayReactions = ReturnType<typeof useCommentReactions>['reactions']
  export type DisplayReaction = NotNull<DisplayReactions[Type]>

  export const Button = ({type, onClick, active, byProfiles, count = 0}: DisplayReaction) => {
    const {Icon, color, anon} = md[type]
    const others = byProfiles?.filter(p => !p.isYou)
    const countOthers = count - (active ? 1 : 0)
    return (
      <Tooltip
        multiline
        label={
          <div className='flex gap-1 wrap'>
            {count == 0 ?
              <>React with {type}</>
            : <>
                {active ?
                  <>
                    <b>You</b>
                    {countOthers > 0 && <> and </>}
                  </>
                : <></>}
                {countOthers > 0 ?
                  <>
                    <b>{countOthers}</b>
                    <span>others</span>
                    {others && <SAvatarGroup profiles={others} />}
                    <span>reacted</span>
                    <b>{type}</b>
                  </>
                : <div>
                    reacted <b>{type}</b>
                  </div>
                }
              </>
            }
            {anon && <div className='opacity-80'> (anonymously)</div>}
          </div>
        }>
        <div>
          <MButton onClick={onClick} size='compact-sm' variant={active ? 'light' : 'subtle'}>
            <Icon color={color ? `var(--mantine-color-${color}-6)` : undefined} />
            {count ?
              <span className='ml-2 font-bold'>{count}</span>
            : <></>}
          </MButton>
        </div>
      </Tooltip>
    )
  }
}
